import "@/App.css";
import React, { useState, useEffect, useRef, createContext, useContext, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { SeoHead } from "@/components/seo/SeoHead";
import { SeoProvider } from "@/components/seo/SeoContext";
import { queryClient } from "@/lib/query/client";
import { Toaster } from "@/components/ui/sonner";
import "@/i18n"; // Initialize i18n
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LimitsProvider } from "@/contexts/LimitsContext";
import { ActionBarProvider } from "@/contexts/ActionBarContext";
import { ConfigProvider, useConfig } from "@/contexts/ConfigContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
const CompleteProfileModal = lazy(() => import("@/components/CompleteProfileModal").then(m => ({ default: m.CompleteProfileModal })));
const NotificationToastContainer = lazy(() => import("@/components/NotificationToastContainer").then(m => ({ default: m.NotificationToastContainer })));
const EnvironmentBadge = lazy(() => import("@/components/EnvironmentBadge").then(m => ({ default: m.EnvironmentBadge })));
import SiteAccessGuard from "@/components/SiteAccessGuard";
import { analyticsAllowed, CONSENT_EVENT } from "@/lib/consent";
import api from '@/lib/api';
import { checkForNewDeploy } from "@/lib/staleDeployGuard";

import { useClarity } from "@/hooks/useClarity";

// ============================================================
// CRITICAL PAGES - Loaded immediately (auth flow, landing)
// ============================================================
const AuthCallback = lazy(() => import("@/pages/AuthCallback").then(m => ({ default: m.AuthCallback })));
const GoogleLinkCallback = lazy(() => import("@/pages/GoogleLinkCallback").then(m => ({ default: m.GoogleLinkCallback })));
const Login = lazy(() => import("@/pages/Login").then(m => ({ default: m.Login })));
const Register = lazy(() => import("@/pages/Register").then(m => ({ default: m.Register })));
const Comunidade = lazy(() => import("@/pages/Comunidade").then(m => ({ default: m.Comunidade })));
import { BACKEND_URL } from "./config";

// ============================================================
// LAZY LOADED PAGES - Loaded on demand
// ============================================================

// Auth pages (less critical - only after login attempt)
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword").then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("@/pages/ResetPassword").then(m => ({ default: m.ResetPassword })));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail").then(m => ({ default: m.VerifyEmail })));
const ChangePassword = lazy(() => import("@/pages/ChangePassword").then(m => ({ default: m.ChangePassword })));
const ConfirmEmailChange = lazy(() => import("@/pages/ConfirmEmailChange").then(m => ({ default: m.ConfirmEmailChange })));
const PendingApproval = lazy(() => import("@/pages/PendingApproval").then(m => ({ default: m.PendingApproval })));
const RegisterCTA = lazy(() => import("@/pages/RegisterCTA"));
const MobileRegisterSuccess = lazy(() => import("@/pages/MobileRegisterSuccess"));

// Public pages
const TripsPage = lazy(() => import("@/pages/TripsPage").then(m => ({ default: m.TripsPage })));
const ClubsPage = lazy(() => import("@/pages/ClubsPage").then(m => ({ default: m.ClubsPage })));
const CornerDetail = lazy(() => import("@/pages/CornerDetail"));
const TripDetail = lazy(() => import("@/pages/TripDetail").then(m => ({ default: m.TripDetail })));
const ClubDetail = lazy(() => import("@/pages/ClubDetail").then(m => ({ default: m.ClubDetail })));
const EventPublicPage = lazy(() => import("@/pages/EventPublicPage"));
const MotorcycleDetail = lazy(() => import("@/pages/MotorcycleDetail"));
const Garage = lazy(() => import("@/pages/Garage").then(m => ({ default: m.Garage })));

// Static pages
const About = lazy(() => import("@/pages/About"));
const HelpCenter = lazy(() => import("@/pages/HelpCenter"));
const HelpCategory = lazy(() => import("@/pages/HelpCategory"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Contact = lazy(() => import("@/pages/Contact"));
const PricingPage = lazy(() => import("@/pages/PricingPageV2"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogArticlePage = lazy(() => import("@/pages/BlogArticlePage"));
const MaintenancePage = lazy(() => import("@/pages/MaintenancePage"));
const ExportDownload = lazy(() => import("@/pages/ExportDownload"));
const ConfirmAccountClosure = lazy(() => import("@/pages/ConfirmAccountClosure"));
const CancelAccountClosure = lazy(() => import("@/pages/CancelAccountClosure"));

// Protected pages (heavy)
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const GarageEdit = lazy(() => import("@/pages/GarageEdit").then(m => ({ default: m.GarageEdit })));
const MyClubsEdit = lazy(() => import("@/pages/MyClubsEdit").then(m => ({ default: m.MyClubsEdit })));
const MyTripsEdit = lazy(() => import("@/pages/MyTripsEdit"));
const ClubTripsEdit = lazy(() => import("@/pages/ClubTripsEdit"));

const Preferences = lazy(() => import("@/pages/Preferences"));
const TripFolderManager = lazy(() => import("@/pages/TripFolderManager"));
const CornerEdit = lazy(() => import("@/pages/CornerEdit"));
const MyCollaborationsPage = lazy(() => import("@/pages/MyCollaborationsPage"));
const MyParticipationsPage = lazy(() => import("@/pages/MyParticipationsPage"));
const MyClubMemberships = lazy(() => import("@/pages/MyClubMemberships").then(m => ({ default: m.MyClubMemberships })));
const ReferralsPage = lazy(() => import("@/pages/ReferralsPage"));
const ReferralRedirect = lazy(() => import("@/pages/ReferralRedirect"));
const MotriPage = lazy(() => import("@/pages/MotriPage"));
const MotriLandingPage = lazy(() => import("@/pages/MotriLandingPage"));
const GoLandingPage = lazy(() => import("@/pages/GoLandingPage"));
const MyRoutesPage = lazy(() => import("@/pages/MyRoutesPage"));
const GoRouteDetail = lazy(() => import("@/pages/GoRouteDetail"));
const PublicRoutePage = lazy(() => import("@/pages/PublicRoutePage"));
const PublicRoutesGalleryPage = lazy(() => import("@/pages/PublicRoutesGalleryPage"));
const RoutesToolsEditorPage = lazy(() => import("@/pages/RoutesTools/EditorPage"));
const EditorLandingPage = lazy(() => import("@/pages/EditorLandingPage"));
const TripGallery = lazy(() => import("@/pages/TripGallery"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// ============================================================
// Loading Fallback Component
// ============================================================
// CSS-only spinner to avoid pulling lucide-react into the initial bundle
const Spinner = ({ className = "h-8 w-8" }) => (
  <div className={`${className} animate-spin rounded-full border-2 border-muted border-t-primary`} />
);

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <Spinner className="h-8 w-8" />
    </div>
  </div>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// Context for security settings
const SecurityContext = createContext({ recaptcha_enabled: false });

// Context for profile completion
const ProfileContext = createContext({ showProfileModal: false, checkProfile: () => {} });
export const useProfile = () => useContext(ProfileContext);

// Redirect component for /motard/:userId/:slug -> /meu-canto?motard=userId
function MotardRedirect() {
  const { userId } = useParams();
  return <Navigate to={`/meu-canto?motard=${userId}`} replace />;
}

// Redirect component for legacy /grupo/:clubId routes -> /clube/:clubId
function ClubDetailRedirect() {
  const { clubId, slug } = useParams();
  const location = window.location;
  // Preserve the slug if present
  const newPath = slug ? `/clube/${clubId}/${slug}` : `/clube/${clubId}`;
  // Preserve query string if any
  const queryString = location.search || '';
  return <Navigate to={`${newPath}${queryString}`} replace />;
}

// Redirect component for /grupo/:clubId/editar -> /meu-canto/editar?tab=clubes&subtab=my-groups&editgroup=clubId
function ClubEditRedirect() {
  const { clubId } = useParams();
  return <Navigate to={`/meu-canto/editar?tab=clubes&subtab=my-groups&editgroup=${clubId}`} replace />;
}

// Redirect component for /passeio/:tripId/editar -> inline edit based on trip type and user role
function TripEditRedirect() {
  const { tripId } = useParams();
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTripType = async () => {
      try {
        const API_URL = BACKEND_URL;
        
        // First get current user
        const userResponse = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include'
        });
        const currentUser = userResponse.ok ? await userResponse.json() : null;
        
        // Then get trip details
        const response = await fetch(`${API_URL}/api/trips/${tripId}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const trip = await response.json();
          
          // Check if user is the trip owner
          const isOwner = currentUser && trip.user_id === currentUser.user_id;
          
          // Check if user is a collaborator (not owner)
          const isCollaborator = currentUser && !isOwner && trip.collaborators?.some(
            c => c.user_id === currentUser.user_id && c.status === 'accepted'
          );
          
          if (isCollaborator) {
            // Collaborator - redirect to collaborations page
            setRedirectUrl(`/minhas-colaboracoes?edit=${tripId}`);
          } else if (trip.owner_type === 'club' && trip.club_id) {
            // Club trip (owner/moderator) - redirect to club trips edit
            setRedirectUrl(`/passeios-grupo/editar?group=${trip.club_id}&edit=${tripId}`);
          } else {
            // Personal trip (owner) - redirect to my trips edit
            setRedirectUrl(`/meus-passeios/editar?tab=personal&edit=${tripId}`);
          }
        } else {
          // Fallback to personal trips
          setRedirectUrl(`/meus-passeios/editar?tab=personal&edit=${tripId}`);
        }
      } catch (error) {
        // Fallback to personal trips on error
        setRedirectUrl(`/meus-passeios/editar?tab=personal&edit=${tripId}`);
      }
      setLoading(false);
    };
    fetchTripType();
  }, [tripId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return <Navigate to={redirectUrl} replace />;
}

// Clarity Analytics Tracker Component
function ClarityTracker() {
  useClarity();
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  const firstNavRef = useRef(true);
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  // STALE-DEPLOY-GUARD (2026-07-23): on each navigation (except the very
  // first), check if a NEW bundle has been deployed since this tab loaded. If
  // so, reload so the user silently picks up the fresh code instead of running
  // a stale bundle (the "only works after a manual refresh" symptom). The
  // baseline is captured once at startup, so after the reload this no-ops.
  useEffect(() => {
    if (firstNavRef.current) { firstNavRef.current = false; return; }
    let cancelled = false;
    checkForNewDeploy().then((isNew) => {
      if (!cancelled && isNew) window.location.reload();
    });
    return () => { cancelled = true; };
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id - synchronous check before routes
  if (location.hash?.includes('session_id=')) {
    // Check if this is a link callback (opened in popup for account linking)
    if (location.pathname === '/auth/google/link-callback') {
      return <GoogleLinkCallback />;
    }
    return <AuthCallback />;
  }
  
  return (
    <>
      <ScrollToTop />
      <ClarityTracker />
      <Suspense fallback={<PageLoader />}>
        <Routes>
        {/* Maintenance page — manual admin mode only */}
        <Route path="/manutencao" element={<MaintenancePage />} />
        {/* Auth routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/google/link-callback" element={<GoogleLinkCallback />} />
        <Route path="/registar" element={<Register />} />
        <Route path="/registar/sucesso-mobile" element={<MobileRegisterSuccess />} />
        <Route path="/regista-te" element={<RegisterCTA />} />
        <Route path="/esqueci-password" element={<ForgotPassword />} />
        <Route path="/repor-password" element={<ResetPassword />} />
        <Route path="/verificar-email" element={<VerifyEmail />} />
        <Route path="/alterar-password" element={<ChangePassword />} />
        <Route path="/confirmar-email" element={<ConfirmEmailChange />} />
        <Route path="/aguardar-aprovacao" element={<PendingApproval />} />
        
        {/* Public routes - accessible to everyone */}
        <Route path="/" element={<Comunidade />} />
        <Route path="/comunidade" element={<Navigate to="/" replace />} />
        <Route path="/entrada" element={<Navigate to="/" replace />} />
        <Route path="/passeio/:tripId" element={<TripDetail />} />
        <Route path="/passeio/:tripId/galeria" element={<TripGallery />} />
        <Route path="/passeio/:tripId/:slug" element={<TripDetail />} />
        
        {/* Public routes - accessible to anonymous (with limitations) */}
        <Route path="/passeios" element={<TripsPage />} />
        <Route path="/clubes" element={<ClubsPage />} />
        <Route path="/clube/:clubId" element={<ClubDetail />} />
        <Route path="/clube/:clubId/:slug" element={<ClubDetail />} />
        <Route path="/evento/:eventId" element={<EventPublicPage />} />
        <Route path="/evento/:eventId/:slug" element={<EventPublicPage />} />
        {/* Legacy redirects for /grupos and /grupo */}
        <Route path="/grupos" element={<Navigate to="/clubes" replace />} />
        <Route path="/grupo/:clubId" element={<ClubDetailRedirect />} />
        <Route path="/grupo/:clubId/:slug" element={<ClubDetailRedirect />} />
      
      {/* Redirect /motard/:userId/:slug to /meu-canto?motard=userId */}
      <Route path="/motard/:userId" element={<MotardRedirect />} />
      <Route path="/motard/:userId/:slug" element={<MotardRedirect />} />
      
      {/* Protected routes - require authentication */}
      {/* Redirect /clube/novo and /grupo/novo to /meus-clubes */}
      <Route path="/clube/novo" element={<Navigate to="/meus-clubes" replace />} />
      <Route path="/grupo/novo" element={<Navigate to="/meus-clubes" replace />} />
      <Route path="/minhas-colaboracoes" element={
        <ProtectedRoute>
          <MyCollaborationsPage />
        </ProtectedRoute>
      } />
      <Route path="/minhas-participacoes" element={
        <ProtectedRoute>
          <MyParticipationsPage />
        </ProtectedRoute>
      } />
      <Route path="/clube/:clubId/editar" element={
        <ProtectedRoute>
          <ClubEditRedirect />
        </ProtectedRoute>
      } />
      {/* Legacy redirect for /grupo/:clubId/editar */}
      <Route path="/grupo/:clubId/editar" element={
        <ProtectedRoute>
          <ClubEditRedirect />
        </ProtectedRoute>
      } />
      <Route path="/apresentacao" element={<About />} />
      <Route path="/sobre" element={<Navigate to="/apresentacao" replace />} />
      <Route path="/ajuda" element={<HelpCenter />} />
      <Route path="/ajuda/:category" element={<HelpCategory />} />
      <Route path="/faq" element={<Navigate to="/ajuda" replace />} />
      <Route path="/privacidade" element={<Privacy />} />
      <Route path="/termos" element={<Terms />} />
      <Route path="/contactos" element={<Contact />} />
      <Route path="/planos" element={<PricingPage />} />
      <Route path="/motoblogs" element={<BlogPage />} />
      <Route path="/motoblogs/:slug" element={<BlogArticlePage />} />
      <Route path="/blog" element={<Navigate to="/motoblogs" replace />} />
      <Route path="/blog/:slug" element={<Navigate to="/motoblogs" replace />} />
      <Route path="/motri-apresentacao" element={<MotriLandingPage />} />
      <Route path="/go" element={<GoLandingPage />} />
      {/* 2026-06-19 — "Ferramentas" passou a "Editor de Rotas". A landing
          pública vive agora em /ferramentas/editor; /ferramentas redireciona
          (301-like) para lá, deixando o caminho aberto a um futuro índice de
          ferramentas. */}
      <Route path="/ferramentas" element={<Navigate to="/ferramentas/editor" replace />} />
      <Route path="/ferramentas/editor" element={<EditorLandingPage />} />
      <Route path="/ref/:code" element={<ReferralRedirect />} />
      <Route path="/export/:token" element={<ExportDownload />} />
      <Route path="/confirmar-fecho-conta" element={<ConfirmAccountClosure />} />
      <Route path="/cancelar-fecho-conta" element={<CancelAccountClosure />} />
      <Route path="/fecho-cancelado" element={<CancelAccountClosure />} />
      
      {/* Corner - public with motard param, protected for own corner */}
      <Route path="/meu-canto" element={<CornerDetail />} />
      
      {/* Profile - Account management (security, export, activities, etc.) */}
      <Route path="/perfil" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      {/* Redirect standalone trip routes to inline forms */}
      <Route path="/passeio/novo" element={
        <ProtectedRoute>
          <Navigate to="/meus-passeios/editar?tab=create" replace />
        </ProtectedRoute>
      } />
      <Route path="/passeio/:tripId/editar" element={
        <ProtectedRoute>
          <TripEditRedirect />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPanel />
        </ProtectedRoute>
      } />
      {/* Garage — redirects to Corner (garage is now inside Corner) */}
      <Route path="/garagem" element={<Navigate to="/meu-canto" replace />} />
      <Route path="/garagem/editar" element={<Navigate to="/meu-canto/editar?tab=motas" replace />} />
      {/* My Clubs pages - only edit page, no public view */}
      <Route path="/meus-clubes" element={
        <ProtectedRoute>
          <MyClubsEdit />
        </ProtectedRoute>
      } />
      {/* Legacy redirect for /meus-grupos */}
      <Route path="/meus-grupos" element={<Navigate to="/meus-clubes" replace />} />
      <Route path="/minhas-adesoes" element={
        <ProtectedRoute>
          <MyClubMemberships />
        </ProtectedRoute>
      } />
      <Route path="/meus-clubes/passeios" element={
        <ProtectedRoute>
          <ClubTripsEdit />
        </ProtectedRoute>
      } />
      {/* Legacy redirect for /meus-grupos/passeios */}
      <Route path="/meus-grupos/passeios" element={<Navigate to="/meus-clubes/passeios" replace />} />
      <Route path="/passeios-grupo/editar" element={
        <ProtectedRoute>
          <ClubTripsEdit />
        </ProtectedRoute>
      } />
      <Route path="/meus-passeios" element={
        <ProtectedRoute>
          <MyTripsEdit />
        </ProtectedRoute>
      } />
      <Route path="/meus-passeios/editar" element={
        <ProtectedRoute>
          <MyTripsEdit />
        </ProtectedRoute>
      } />
      {/* Old motorcycle create/edit routes - now handled inside Corner edit */}
      <Route path="/mota/nova" element={
        <ProtectedRoute>
          <Navigate to="/meu-canto/editar?tab=motas" replace />
        </ProtectedRoute>
      } />
      <Route path="/mota/:motorcycleId/editar" element={
        <ProtectedRoute>
          <Navigate to="/meu-canto/editar?tab=motas" replace />
        </ProtectedRoute>
      } />
      {/* Motorcycle view page - protected */}
      <Route path="/mota/:motorcycleId" element={<MotorcycleDetail />} />
      <Route path="/mota/:motorcycleId/:slug" element={<MotorcycleDetail />} />
      <Route path="/preferencias" element={
        <ProtectedRoute>
          <Preferences />
        </ProtectedRoute>
      } />
      <Route path="/embaixador" element={
        <ProtectedRoute>
          <ReferralsPage />
        </ProtectedRoute>
      } />
      <Route path="/motri" element={
        <ProtectedRoute>
          <MotriPage />
        </ProtectedRoute>
      } />
      <Route path="/pasta-passeios" element={
        <ProtectedRoute>
          <TripFolderManager />
        </ProtectedRoute>
      } />
      <Route path="/meu-canto/editar" element={
        <ProtectedRoute>
          <CornerEdit />
        </ProtectedRoute>
      } />
      {/* Legacy: a antiga lista "As Minhas Rotas" da Go foi descontinuada;
          a biblioteca unificada vive em /gestaorotas. */}
      <Route path="/minhas-rotas-go" element={<Navigate to="/gestaorotas" replace />} />
      {/* Fase D · "As Minhas Rotas" — unified Rotas library (all origins:
          go_recording, motri_loop_*, editor, passeio_export). */}
      <Route path="/gestaorotas" element={
        <ProtectedRoute>
          <MyRoutesPage />
        </ProtectedRoute>
      } />
      {/* Legacy path redirect (renamed to /gestaorotas) */}
      <Route path="/as-minhas-rotas" element={<Navigate to="/gestaorotas" replace />} />
      <Route path="/rota-go/:tripId" element={
        <ProtectedRoute>
          <GoRouteDetail />
        </ProtectedRoute>
      } />
      {/* PUBLIC-DISCOVERY (Fase 2) — public read-only Rota page.
          No auth: anyone can view a public/unlisted route by URL.
          Slug shape `/rota/{route_id}-{name-slug}` (slug cosmetic). */}
      <Route path="/rota/:slug" element={<PublicRoutePage />} />
      {/* ROUTES-GALLERY — listagem pública de rotas (filtros avançados em standby) */}
      <Route path="/rotas" element={<PublicRoutesGalleryPage />} />
      {/* /rotas/ferramentas redirects to the editor so the avatar-menu
          item ("Ferramentas") lands directly on the map-canvas. The
          dashboard was retired 2026-05-10 when we adopted the
          single-canvas philosophy (see Editor for details). */}
      <Route path="/rotas/ferramentas" element={<Navigate to="/rotas/ferramentas/editor" replace />} />
      <Route path="/rotas/ferramentas/editor" element={
        <ProtectedRoute>
          <RoutesToolsEditorPage />
        </ProtectedRoute>
      } />
      {/* Legacy /visualizar route — pre-dashboard-retirement bookmarks
          forward to the editor. Safe to remove after a few weeks. */}
      <Route path="/rotas/ferramentas/visualizar" element={<Navigate to="/rotas/ferramentas/editor" replace />} />
      
      {/* Legacy routes - redirect to inline forms */}
      <Route path="/trip/new" element={<Navigate to="/meus-passeios/editar?tab=create" replace />} />
      <Route path="/trip/:tripId" element={<Navigate to="/passeio/:tripId" replace />} />
      <Route path="/trip/:tripId/edit" element={
        <ProtectedRoute>
          <TripEditRedirect />
        </ProtectedRoute>
      } />
      <Route path="/viagem/nova" element={<Navigate to="/meus-passeios/editar?tab=create" replace />} />
      <Route path="/viagem/:tripId" element={<Navigate to="/passeio/:tripId" replace />} />
      <Route path="/viagem/:tripId/editar" element={
        <ProtectedRoute>
          <TripEditRedirect />
        </ProtectedRoute>
      } />
      {/* / is now served by Comunidade directly (defined above) */}
      {/* 404 catch-all — must be last */}
      <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  );
}

// Wrapper component for reCAPTCHA provider - uses ConfigContext for settings
function RecaptchaWrapper({ children }) {
  const { recaptchaEnabled, loaded } = useConfig();

  // Show children while ConfigContext is loading (don't block the app)
  if (!loaded) {
    return (
      <SecurityContext.Provider value={{ recaptcha_enabled: false }}>
        {children}
      </SecurityContext.Provider>
    );
  }

  // Skip reCAPTCHA if disabled in settings
  if (!recaptchaEnabled) {
    return (
      <SecurityContext.Provider value={{ recaptcha_enabled: false }}>
        {children}
      </SecurityContext.Provider>
    );
  }
  
  if (!RECAPTCHA_SITE_KEY) {
    return (
      <SecurityContext.Provider value={{ recaptcha_enabled: false }}>
        {children}
      </SecurityContext.Provider>
    );
  }
  
  return (
    <SecurityContext.Provider value={{ recaptcha_enabled: true }}>
      <GoogleReCaptchaProvider
        reCaptchaKey={RECAPTCHA_SITE_KEY}
        language="pt-PT"
        scriptProps={{
          async: true,
          defer: true,
        }}
      >
        {children}
      </GoogleReCaptchaProvider>
    </SecurityContext.Provider>
  );
}

// Export hook for components to check if reCAPTCHA is enabled
export const useSecuritySettings = () => useContext(SecurityContext);

// Loading fallback for i18n
function I18nLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Spinner className="h-10 w-10" />
    </div>
  );
}

// Tawk.to Widget IDs per language
const TAWK_WIDGETS = {
  pt: '1jgptnaqt',
  en: '1jgqbdgdh',
  es: '1jgqccqh9',
  fr: '1jgqbtpgh'
};
const TAWK_PROPERTY_ID = '69861b8ffcf37c1c39798945';

// Tawk.to Chat Widget Loader - delayed to not block initial render
function useTawkTo() {
  const { user } = useAuth();
  const configCtx = useConfig();
  const location = useLocation();

  // Tawk.to sets third-party cookies → gated behind cookie consent.
  const [consentTick, setConsentTick] = useState(0);
  useEffect(() => {
    const h = () => setConsentTick((t) => t + 1);
    window.addEventListener(CONSENT_EVENT, h);
    return () => window.removeEventListener(CONSENT_EVENT, h);
  }, []);
  
  const getLang = () => (localStorage.getItem('i18nextLng') || 'pt').substring(0, 2);
  const widgetId = TAWK_WIDGETS[getLang()] || TAWK_WIDGETS.pt;
  const tawkMode = configCtx?.features?.tawkMode || "hidden";
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isContactPage = location.pathname === '/contactos';

  // Determine if widget bubble should be visible
  const showBubble = tawkMode === "all_pages" || (tawkMode === "contacts_only" && isContactPage);
  
  // Toggle widget visibility on route changes (for contacts_only mode)
  useEffect(() => {
    if (isLocalhost || tawkMode === "hidden") return;
    try {
      if (showBubble) {
        window.Tawk_API?.showWidget?.();
      } else {
        window.Tawk_API?.hideWidget?.();
      }
    } catch {}
  }, [isLocalhost, tawkMode, showBubble]);

  useEffect(() => {
    // Never load on localhost
    if (isLocalhost) return;
    // GDPR: don't load Tawk.to until the user has consented to non-essential cookies
    if (!analyticsAllowed()) return;
    // Don't load script if mode is hidden — Tawk is still available via custom clicks
    // Actually, we ALWAYS load the script so custom clicks work, but control bubble visibility
    
    // Script already loaded — just toggle bubble visibility
    if (document.getElementById('tawk-script')) {
      return;
    }
    
    const timeoutId = setTimeout(() => {
      window.Tawk_API = window.Tawk_API || {};
      window.Tawk_LoadStart = new Date();

      window.Tawk_API.customStyle = {
        visibility: {
          desktop: showBubble ? { position: 'br', xOffset: 10, yOffset: 20 } : { position: 'br', xOffset: 9999, yOffset: 9999 },
          mobile: { position: 'br', xOffset: 9999, yOffset: 9999 }
        }
      };
      
      const loadWidget = (visitorData) => {
        if (visitorData) {
          window.Tawk_API.visitor = { name: visitorData.name, email: visitorData.email, hash: visitorData.hash };
        }
        
        window.Tawk_API.onLoad = function() {
          if (showBubble) {
            window.Tawk_API.minimize();
          } else {
            window.Tawk_API.hideWidget();
          }
          if (visitorData && window.Tawk_API.setAttributes) {
            window.Tawk_API.setAttributes({ name: visitorData.name, email: visitorData.email, hash: visitorData.hash }, function() {});
          }
        };

        // Re-hide bubble when chat is closed (if mode requires hidden bubble)
        window.Tawk_API.onChatMinimized = function() {
          const currentMode = configCtx?.features?.tawkMode || "hidden";
          const onContacts = window.location.pathname === '/contactos';
          const shouldShow = currentMode === "all_pages" || (currentMode === "contacts_only" && onContacts);
          if (!shouldShow) {
            window.Tawk_API.hideWidget();
          }
        };
        
        const script = document.createElement('script');
        script.async = true;
        script.id = 'tawk-script';
        script.src = `https://embed.tawk.to/${TAWK_PROPERTY_ID}/${widgetId}`;
        script.charset = 'UTF-8';
        document.body.appendChild(script);
      };
      
      if (user?.email) {
        fetch(`${BACKEND_URL}/api/tawk-hash`, { credentials: 'include' })
          .then(res => res.ok ? res.json() : null)
          .then(loadWidget)
          .catch(() => loadWidget(null));
      } else {
        loadWidget(null);
      }
    }, 2000);
    
    return () => clearTimeout(timeoutId);
  }, [user?.email, widgetId, isLocalhost, consentTick]); // eslint-disable-line react-hooks/exhaustive-deps
}

// Component
function TawkToWidget() {
  useTawkTo();
  return null;
}

// Profile Gate - blocks content until profile is complete
function ProfileGate({ children }) {
  const { user, loading, showProfileModal, setShowProfileModal, refreshUser } = useAuth();
  
  const handleProfileComplete = async () => {
    await refreshUser();
    setShowProfileModal(false);
  };
  
  // During initial auth loading, show nothing (prevent flash)
  if (loading) {
    return null;
  }
  
  // If user needs to complete profile, show ONLY the modal (block content)
  if (user && showProfileModal) {
    return (
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Spinner className="h-8 w-8" /></div>}>
        <CompleteProfileModal 
          isOpen={true} 
          onComplete={handleProfileComplete}
          initialData={user}
        />
      </Suspense>
    );
  }
  
  // Profile complete or not logged in - show content
  return children;
}

// Inner app component that can use ConfigContext
const BetaBanner = lazy(() => import("@/components/BetaBanner").then(m => ({ default: m.BetaBanner })));
const LanguageBanner = lazy(() => import("@/components/LanguageBanner").then(m => ({ default: m.LanguageBanner })));
const CookieConsent = lazy(() => import("@/components/CookieConsent").then(m => ({ default: m.CookieConsent })));

function AppContent() {
  // NOTE: We intentionally do NOT strip static SEO tags here on mount.
  // react-helmet-async (<SeoHead/> global + per-page) is the single source
  // of truth for head tags and manages/de-dupes them on its own. A previous
  // cleanup useEffect raced Helmet's async commit and wiped Helmet's own
  // tags (verified regression + Emergent Support guidance). The served
  // index.html must NOT bake a static <link rel=canonical>; Helmet provides
  // the canonical, title, description and OG tags.
  return (
    <div className="App min-h-screen bg-background">
      <RecaptchaWrapper>
        <BrowserRouter>
          <SeoHead />
          <AuthProvider>
            <LimitsProvider>
            <SiteAccessGuard>
              <ProfileGate>
                <Suspense fallback={null}><LanguageBanner /></Suspense>
                <Suspense fallback={null}><BetaBanner /></Suspense>
                <Suspense fallback={null}><CookieConsent /></Suspense>
                <TawkToWidget />
                <ActionBarProvider>
                  <AppRouter />
                  <Suspense fallback={null}><NotificationToastContainer /></Suspense>
                </ActionBarProvider>
              </ProfileGate>
            </SiteAccessGuard>
            </LimitsProvider>
          </AuthProvider>
          {/* Toaster must live INSIDE BrowserRouter so toast payloads can
              contain <RouterLink>/useNavigate consumers (e.g. the success
              toast in SaveAsRotaDialog renders a "Ver" link to
              /as-minhas-rotas). Sonner renders to a portal anyway, so
              z-index/positioning aren't affected by this position. */}
          <Toaster position="bottom-center" richColors duration={6000} />
        </BrowserRouter>
      </RecaptchaWrapper>
      <Suspense fallback={null}><EnvironmentBadge /></Suspense>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<I18nLoadingFallback />}>
        <ConfigProvider>
          <SeoProvider>
            <AppContent />
          </SeoProvider>
        </ConfigProvider>
      </Suspense>
      {/* DevTools disabled - locale conflict with i18n. Enable locally if needed. */}
    </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
